import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_SORT_BY, STATIC_COLUMNS } from '~/common/constants/search';
import BaseLoadView from '~/components/Shared/BaseLoadView/BaseLoadView';
import BaseSaveView from '~/components/Shared/BaseSaveView/BaseSaveView';
import { Box } from '~/eds/src/components/Box/Box';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { TableViewContextType } from '~/enums/TableViewType';
import { Filter } from '~/evifields/src/types/Filter';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useDocumentDetails } from '~/hooks/useDocumentDetails';
import { usePinnedFilters } from '~/hooks/usePinnedFilters';
import { useRecentSearches } from '~/hooks/useRecentSearches';
import { useSearchNavigation } from '~/hooks/useSearchNavigation';
import { useTableState } from '~/hooks/useTableState';
import { useTrackSegment } from '~/hooks/useTrackSegment ';
import { api } from '~/redux/api/createApi';
import { SearchItem } from '~/redux/api/methods/evisearch';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import {
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
} from '~/types/modal.types';
import { Nullable } from '~/types/Nullable';
import { testIsAdmin } from '~/utils/user';

import {
  DEFAULT_SEARCH_ID,
  getDefaultChatContext,
} from '../ask-anything/ask-anything-button/utils';
import { SaveActionButton } from '../search/SaveActionButton/SaveActionButton';
import { SaveEditEntityModal } from '../search/SaveEditEntityModal/SaveEditEntityModal';
import { useDocumentsBulkActionsPanel } from '../search/useDocumentsBulkActionsPanel';
import {
  getDocumentHandlers,
  testIsQueryBuilderSupported,
  toPinnedFilters,
  toTableViewFields,
} from './AdvancedSearch.utils';
import { BooleanSearchBar } from './boolean-search-bar/BooleanSearchBar';
import { SEARCH_ENTITY } from './constants';
import { Filters } from './filters/Filters';
import { attachRenderersAndSerializers } from './filters/Filters.utils';
import { QueryBuilder } from './query-builder/QueryBuilder';
import { ReadOnlyComplexQuery } from './read-only-complex-query/ReadOnlyComplexQuery';
import { ResultsTable } from './results-table/ResultsTable';
import { SearchQuery } from './types';
import { useSaveSearch } from './useSaveSearch';
import { buildQuery } from './utils/build-query';

export const AdvancedSearch = () => {
  const dispatch = useDispatch();
  const trackSegment = useTrackSegment();
  const { navigate, params } = useRouting();
  const { searchId = DEFAULT_SEARCH_ID } = params;
  const currentUser = useCurrentUser();
  const intl = useIntl();
  const isDefaultSearch = !searchId || searchId === DEFAULT_SEARCH_ID;

  const queryBuilder = useSelector(
    slices.searchV3.selectors.selectQueryBuilder,
  );

  const queryBuilderPanelVisible = useSelector(
    slices.searchV3.selectors.selectQueryBuilderPanelVisible,
  );
  const searchText = useSelector(slices.searchV3.selectors.selectSearchText);
  const resultsCache = useSelector(
    slices.searchV3.selectors.selectResultsCache,
  );
  const selectedFilters = useSelector(
    slices.searchV3.selectors.selectSelectedFilters,
  );
  const featuredClauses = useSelector(
    slices.searchV3.selectors.selectFeaturedClauses,
  );

  const isInitialized = useSelector(
    slices.searchV3.selectors.selectIsInitialized,
  );

  const isSearchDirty = useSelector(
    slices.searchV3.selectors.selectIsSearchDirty,
  );
  const activeFilters = useSelector(
    slices.searchV3.selectors.selectActiveFilters,
  );
  const shouldPerformSearch =
    isInitialized || activeFilters.length > 0 || queryBuilder;
  const [documentDetails, setDocumentDetails] =
    useState<Nullable<SearchItem>>(null);
  const {
    tableState,
    handleUpdateTableState,
    partialUpdateTableState,
    clearSelectedRows,
  } = useTableState({
    id: `ResultsTable:${searchId}`,
    columnOrder: [],
    sortBy: DEFAULT_SORT_BY[SEARCH_ENTITY],
  });
  const initialColumnOrderRef = useRef<string[]>([]);
  const isDirty = useMemo(
    () =>
      isSearchDirty ||
      !isEqual(tableState.columnOrder, initialColumnOrderRef.current),
    [isSearchDirty, tableState.columnOrder],
  );
  const { data: searchFilters, isFetching: isFetchingFilters } =
    api.endpoints.getFiltersV2.useQuery({ type: SEARCH_ENTITY });

  const [fetchCurrentTableView, tableViewResult] =
    api.endpoints.getCurrentTableView.useLazyQuery();
  const { data: tableView, isFetching: isFetchingTableView } = tableViewResult;
  const [updateTableView] = api.endpoints.updateTableView.useMutation();

  const {
    data: savedSearch,
    isLoading: isLoadingSavedSearch,
    error: savedSearchError,
  } = api.endpoints.getSavedSearch.useQuery(searchId, {
    skip: isDefaultSearch,
  });
  const hasSavePermission =
    testIsAdmin(currentUser) || savedSearch?.userId === currentUser.id;
  const isSaveDisabled = !isDirty || !hasSavePermission;

  const isQueryBuilderSupported = useMemo(
    () =>
      testIsQueryBuilderSupported(queryBuilder, savedSearch?.isComplexQuery),
    [savedSearch?.isComplexQuery, queryBuilder],
  );

  const [isLoadViewVisible, setIsLoadViewVisible] = useState(false);
  const [isSaveViewVisible, setIsSaveViewVisible] = useState(false);
  const [isSaveAsVisible, setIsSaveAsVisible] = useState(false);

  const query = useMemo(() => {
    if (!searchFilters) return [];

    return (
      queryBuilder ??
      buildQuery({
        booleanQuery: searchText ?? '',
        filters: activeFilters,
        searchFilters,
      })
    );
  }, [searchText, activeFilters, queryBuilder, searchFilters]);
  const queryRef = useRef(query);
  queryRef.current = query;

  const {
    getPinnedFilters,
    updatePinnedFilters,
    pinnedFiltersIds,
    isFetching: isFetchingPinnedFilters,
  } = usePinnedFilters(searchFilters, TableViewContextType.EVISEARCH_SEARCH);

  const { pushRecentSearch } = useRecentSearches(
    TableViewContextType.EVISEARCH_SEARCH,
  );

  const {
    data: searchResponse,
    isFetching: isFetchingSearch,
    refetch: performSearch,
  } = api.endpoints.search.useQuery(
    {
      pageSize: tableState.pageSize,
      page: tableState.pageIndex,
      sortBy: tableState.sortBy,
      type: SEARCH_ENTITY,
      query,
      filters: [...STATIC_COLUMNS[SEARCH_ENTITY], ...tableState.columnOrder],
    },
    { skip: !shouldPerformSearch },
  );

  const fieldsWithLazyLoad = useMemo(
    () =>
      attachRenderersAndSerializers({
        filters: Object.values(searchFilters || {}),
        type: SEARCH_ENTITY,
      }),
    [searchFilters],
  );

  const [handleNavigate] = useSearchNavigation({
    sortBy: tableState.sortBy,
    query,
  });

  const {
    DetailsPanel,
    panelProps,
    renderMultipleLocationModal,
    openMultipleLocationsModal,
  } = useDocumentDetails({
    index: searchResponse?.results?.indexOf(documentDetails!) ?? -1,
    count: searchResponse?.results?.length ?? 0,
    handlers: getDocumentHandlers(documentDetails),
    featuredClauses,
    fullTextMatches: documentDetails?.highlights?.full_text_matches,
    onHide: () => setDocumentDetails(null),
    onNavigate: (index) =>
      setDocumentDetails(searchResponse?.results?.[index] ?? null),
    onNavigateToDocument: handleNavigate,
  });

  const {
    panel: bulkActionPanel,
    limitExceededModal,
    getBulkActions,
  } = useDocumentsBulkActionsPanel({
    selectedDocumentIds: tableState?.selectedIds,
    isAllSelected: tableState?.isAllSelected,
    totalCount: searchResponse?.meta?.total ?? 0,
    query,
    searchAPI: 'v3',
    onActionCompleted: () => {
      clearSelectedRows();
      performSearch();
    },
    resultsCache,
  });

  const searchName = savedSearch?.name ?? 'Search';

  const handleBooleanSearch = useCallback(
    (value: string) => {
      dispatch(slices.searchV3.actions.setSearchText(value));
      partialUpdateTableState({ pageIndex: 1 });
    },
    [dispatch, partialUpdateTableState],
  );

  const handleFiltersChange = useCallback(
    (filters: Filter[]) => {
      const addedFieldIds = filters
        .filter((f) => !selectedFilters.some((sf) => sf.fieldId === f.fieldId))
        .map((f) => String(f.fieldId));
      if (addedFieldIds.length > 0) {
        trackSegment('selectAddFilters', {
          Name: searchName,
          Value: addedFieldIds.join(','),
        });
      }
      dispatch(slices.searchV3.actions.setSelectedFilters(filters));
      partialUpdateTableState({ pageIndex: 1 });
    },
    [
      selectedFilters,
      trackSegment,
      searchName,
      dispatch,
      partialUpdateTableState,
    ],
  );

  const handleResetFilters = useCallback(() => {
    if (!searchFilters) return;

    if (savedSearch) {
      dispatch(
        slices.searchV3.actions.initSavedSearchData({
          searchFilters,
          pageSize: tableState.pageSize,
          sortBy: tableState.sortBy,
          savedSearch,
        }),
      );
    } else {
      const pinnedFilters = toPinnedFilters(pinnedFiltersIds);
      dispatch(slices.searchV3.actions.setSelectedFilters(pinnedFilters));
    }
    trackSegment('selectClearFilters', { Name: searchName });
    partialUpdateTableState({ pageIndex: 1 });
  }, [
    searchFilters,
    savedSearch,
    tableState.pageSize,
    tableState.sortBy,
    pinnedFiltersIds,
    trackSegment,
    searchName,
    dispatch,
    partialUpdateTableState,
  ]);

  const handleColumnOrderChange = useCallback(
    (columnOrder: string[]) => {
      partialUpdateTableState({ columnOrder });
      if (isDefaultSearch) {
        updateTableView({
          context: TableViewContextType.EVISEARCH_SEARCH,
          fields: toTableViewFields(columnOrder),
        });
      }
    },
    [partialUpdateTableState, isDefaultSearch, updateTableView],
  );

  const openQueryBuilderPanel = useCallback(() => {
    dispatch(slices.searchV3.actions.setQueryBuilderPanelVisible(true));
    trackSegment('selectQueryBuilder', { Name: searchName });
  }, [dispatch, trackSegment, searchName]);

  const hideQueryBuilderPanel = useCallback(() => {
    dispatch(slices.searchV3.actions.setQueryBuilderPanelVisible(false));
  }, [dispatch]);

  const handleSearchQueryBuilder = useCallback(
    (query: SearchQuery) => {
      dispatch(slices.searchV3.actions.setQueryBuilder(query));
      dispatch(slices.searchV3.actions.setQueryBuilderPanelVisible(false));
      dispatch(slices.searchV3.actions.setSearchText(''));
      partialUpdateTableState({ pageIndex: 1 });
    },
    [dispatch, partialUpdateTableState],
  );

  const { handleSave, handleSaveAs } = useSaveSearch({
    savedSearch,
    query,
    selectedFilters,
    columnOrder: tableState.columnOrder,
  });

  useEffect(() => {
    if (searchResponse?.results) {
      dispatch(
        slices.searchV3.actions.patchEvisearchResultsCache(
          searchResponse.results,
        ),
      );

      if (searchResponse.results.length > 0) {
        dispatch(
          slices.chatbot.actions.setContext([
            getDefaultChatContext(intl),
            {
              label: intl.formatMessage({
                id: 'AdvancedSearch.allResultsLabel',
                defaultMessage: 'All Results',
                description: 'Label for all search results context',
              }),
              query,
              title: intl.formatMessage(
                {
                  id: 'AdvancedSearch.allResultsTitle',
                  defaultMessage: 'All Results ({total})',
                  description:
                    'Title showing all results with total count. Example: total = 150',
                },
                {
                  total: searchResponse?.meta?.total,
                },
              ),
              icon: 'search',
              primary: true,
              searchEntity: SEARCH_ENTITY,
              selectedIds: [],
              id: searchId,
              type: 'all_results',
            },
          ]),
        );
      }
    }
  }, [searchResponse, query, searchId, intl]);
  useEffect(() => {
    if (isDefaultSearch) {
      getPinnedFilters()
        .unwrap()
        .then((res) => {
          const pinnedFilters = res?.data
            ? toPinnedFilters(res.data)
            : undefined;
          if (selectedFilters.length === 0) {
            dispatch(
              slices.searchV3.actions.setSelectedFilters(pinnedFilters ?? []),
            );
          }
        });
      fetchCurrentTableView({
        context: TableViewContextType.EVISEARCH_SEARCH,
      });
    } else {
      getPinnedFilters();
    }

    dispatch(
      slices.chatbot.actions.setContext([
        { ...getDefaultChatContext(intl), primary: true },
      ]),
    );

    return () => {
      pushRecentSearch({
        query: queryRef.current,
        datetime: Date.now(),
      });
      dispatch(
        slices.chatbot.actions.setContext([getDefaultChatContext(intl)]),
      );
    };
  }, []);

  useEffect(() => {
    if (tableView?.length) {
      const tableViewFieldIds = tableView.map(
        (tableViewItem) => tableViewItem.field_id,
      ) as string[];

      partialUpdateTableState({ columnOrder: tableViewFieldIds });
    }
  }, [tableView]);

  useEffect(() => {
    if (savedSearch && searchFilters) {
      dispatch(slices.searchV3.actions.setSavedSearch(savedSearch));
      dispatch(
        slices.searchV3.actions.initSavedSearchData({
          searchFilters,
          pageSize: tableState.pageSize,
          sortBy: tableState.sortBy,
          savedSearch,
        }),
      );
      const columnOrder = savedSearch.resultTable.map(
        (column) => column.filterId,
      ) as string[];
      partialUpdateTableState({ columnOrder });
      initialColumnOrderRef.current = columnOrder;
    }
    return () => {
      dispatch(slices.searchV3.actions.setSavedSearch(undefined));
    };
  }, [searchFilters, savedSearch]);

  const columnOrderIds = useMemo(
    () => toTableViewFields(tableState.columnOrder),
    [tableState.columnOrder],
  );

  const placeholderContent = useMemo(
    () =>
      savedSearchError
        ? {
            title: intl.formatMessage({
              id: 'AdvancedSearch.noAccessTitle',
              defaultMessage: 'No Access to Page',
              description: 'Title for no access error page',
            }),
            message: intl.formatMessage({
              id: 'AdvancedSearch.noAccessMessage',
              defaultMessage:
                'The saved search does not exist or you do not have permission to access this saved search. To request permission, contact the user who created the saved search.',
              description: 'Message explaining no access to saved search',
            }),
            image: 'error-page-access',
            action: {
              text: intl.formatMessage({
                id: 'AdvancedSearch.takeHomeButton',
                defaultMessage: 'Take me Home',
                description: 'Button text to navigate to home page',
              }),
              level: 'primary' as const,
              onClick: () => navigate(RoutePathType.Home),
            },
          }
        : undefined,
    [savedSearchError],
  );

  const isLoading =
    isFetchingFilters || isFetchingSearch || isFetchingTableView;
  const hasResults = !!searchResponse?.results?.length;

  return (
    <PageLayout
      loadingContent={{
        isLoading: isLoadingSavedSearch,
      }}
      placeholderContent={placeholderContent}
      title={
        searchId && savedSearch
          ? savedSearch.name
          : intl.formatMessage({
              id: 'AdvancedSearch.searchTitle',
              defaultMessage: 'Search',
              description: 'Default title for search page',
            })
      }
    >
      <Layout direction="column" spacing={4}>
        <Layout direction="row" spacing={4} justify="space-between">
          <Box w="100%">
            <BooleanSearchBar
              value={searchText ?? ''}
              onSubmit={handleBooleanSearch}
              isSubmitting={isLoading || !!queryBuilder}
            />
          </Box>
          <Box>
            <SaveActionButton
              entity="Search"
              saveAsNewAction={{
                disabled: !isDefaultSearch && !isDirty,
                onClick: () => setIsSaveAsVisible(true),
              }}
              overwriteAction={
                !isDefaultSearch
                  ? {
                      onClick: handleSave,
                      tooltip: hasSavePermission
                        ? ''
                        : intl.formatMessage({
                            id: 'AdvancedSearch.noSavePrivilegesTooltip',
                            defaultMessage:
                              'You do not have privileges to save this search.',
                            description:
                              'Tooltip shown when user does not have permission to save a search',
                          }),
                      disabled: isSaveDisabled,
                    }
                  : undefined
              }
            />
          </Box>
        </Layout>

        {queryBuilder && searchFilters ? (
          <ReadOnlyComplexQuery
            queryBuilder={queryBuilder}
            isQueryBuilderSupported={isQueryBuilderSupported}
            searchFilters={searchFilters}
            onClear={() => {
              dispatch(slices.searchV3.actions.setQueryBuilder(undefined));
              dispatch(
                slices.searchV3.actions.setQueryBuilderPanelVisible(false),
              );
            }}
            onEdit={() => {
              dispatch(
                slices.searchV3.actions.setQueryBuilderPanelVisible(true),
              );
            }}
          />
        ) : (
          <Filters
            isLoading={isFetchingFilters || isFetchingPinnedFilters}
            pinnedFiltersIds={pinnedFiltersIds}
            selectedFilters={selectedFilters}
            searchFilters={fieldsWithLazyLoad}
            onQueryButtonClick={openQueryBuilderPanel}
            onChange={handleFiltersChange}
            onFiltersReset={handleResetFilters}
            onUpdatePinnedFilters={(fieldIds) => updatePinnedFilters(fieldIds)}
          />
        )}

        {shouldPerformSearch || isLoading || hasResults ? (
          <ResultsTable
            data={searchResponse}
            id={searchId}
            isLoading={isLoading}
            enablePersistedTable={isDefaultSearch}
            entity={SEARCH_ENTITY}
            query={query}
            searchFilters={searchFilters ?? {}}
            state={{
              columnOrder: tableState.columnOrder,
              pageIndex: tableState.pageIndex,
              pageSize: tableState.pageSize,
              sortBy: [tableState.sortBy],
              selectedRowIds: tableState.selectedIds,
            }}
            getBulkActions={getBulkActions}
            onUpdateState={handleUpdateTableState}
            onSetColumnOrder={handleColumnOrderChange}
            onNavigate={handleNavigate}
            onDocumentNameClick={(row) =>
              openMultipleLocationsModal(getDocumentHandlers(row), false)
            }
            onRowDetailsClick={setDocumentDetails}
            onSaveView={() => setIsSaveViewVisible(true)}
            onLoadView={() => setIsLoadViewVisible(true)}
          />
        ) : (
          <EmptyPage preset="no-search" />
        )}

        {DetailsPanel && <DetailsPanel {...panelProps} />}
        {renderMultipleLocationModal?.()}
        {queryBuilderPanelVisible && (
          <QueryBuilder
            query={queryBuilder}
            pinnedFiltersIds={pinnedFiltersIds}
            searchFilters={fieldsWithLazyLoad}
            onQueryChange={handleSearchQueryBuilder}
            onHide={hideQueryBuilderPanel}
          />
        )}
        {limitExceededModal}
        {bulkActionPanel}

        {isSaveViewVisible && (
          <BaseSaveView
            context={TableViewContextType.EVISEARCH_SEARCH}
            fields={columnOrderIds}
            hideModal={() => setIsSaveViewVisible(false)}
            modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE}
          />
        )}

        {isLoadViewVisible && (
          <BaseLoadView
            context={TableViewContextType.EVISEARCH_SEARCH}
            modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH}
            hideModal={() => setIsLoadViewVisible(false)}
            onViewSelected={() =>
              fetchCurrentTableView({
                context: TableViewContextType.EVISEARCH_SEARCH,
              })
            }
          />
        )}

        <SaveEditEntityModal
          placeholder={intl.formatMessage({
            id: 'AdvancedSearch.savedSearchPlaceholder',
            defaultMessage: 'Saved Search Name',
            description: 'Placeholder text for saved search name input',
          })}
          onSubmit={handleSaveAs}
          onHide={() => setIsSaveAsVisible(false)}
          isVisible={isSaveAsVisible}
          title={intl.formatMessage({
            id: 'AdvancedSearch.saveAsNewSearchTitle',
            defaultMessage: 'Save as New Search',
            description: 'Title for save as new search modal',
          })}
        />
      </Layout>
    </PageLayout>
  );
};
